'use client';

import { useEffect } from 'react';
import ReactDOM from 'react-dom';
import Image from '@/components/Image';
import NormalButton from '../Button/normal';
import report, { ReportEvent } from '@/lib/report';

interface NotificationPermissionModalProps {
  visible: boolean;
  onClose: () => void;
  onEnable: () => void | Promise<void>;
  enableButtonText?: string;
  laterButtonText?: string;
  permissionState?: NotificationPermission;
}

/**
 * 通知权限请求弹窗
 *
 * 根据权限状态显示不同内容:
 * - 'default': 引导用户授权
 * - 'denied': 显示手动开启指引
 * - 'granted': 不显示弹窗
 */
const NotificationPermissionModal = ({
  visible,
  onClose,
  onEnable,
  enableButtonText = 'Enable Now',
  laterButtonText = 'Cancel',
  permissionState = 'default'
}: NotificationPermissionModalProps) => {
  // 上报弹窗显示事件
  useEffect(() => {
    if (visible && permissionState !== 'granted') {
      report(ReportEvent.NOTIFICATION_PERMISSION_MODAL_SHOWN, {
        permissionState,
        timestamp: Date.now(),
      });
    }
  }, [visible, permissionState]);

  if (!visible || permissionState === 'granted') return null;

  const isDenied = permissionState === 'denied';

  // 处理启用按钮点击
  const handleEnableClick = async () => {
    const beforeState = permissionState;
    await onEnable();

    // 获取操作后的权限状态
    const afterState = 'Notification' in window ? Notification.permission : 'unsupported';

    report(ReportEvent.NOTIFICATION_PERMISSION_MODAL_ENABLE_CLICKED, {
      beforeState,
      afterState,
      success: afterState === 'granted',
      timestamp: Date.now(),
    });
  };

  // 处理关闭/取消按钮点击
  const handleDismiss = () => {
    report(ReportEvent.NOTIFICATION_PERMISSION_MODAL_DISMISSED, {
      permissionState,
      timestamp: Date.now(),
    });
    onClose();
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1000 }}
    >
      <div className="relative w-[400px] md:w-[500px] bg-white px-8 pt-8 pb-6 rounded-xl shadow-2xl text-center animate-fade-in">
        {/* Pengi Logo */}
        <Image
          className="absolute -top-10 left-1/2 -translate-x-1/2"
          src="/assets/landing-logo.png"
          alt="penguin"
          width={60}
          height={60}
        />

        <div className="flex flex-col gap-4 w-full justify-center items-center mt-4">
          {/* Title */}
          <h3 className="display-text-bold text-xl text-gray-800">
            Pengi is on an adventure!
          </h3>

          {/* Content based on permission state */}
          {isDenied ? (
            <>
              {/* Manual setup guide for denied state */}
              <div className="w-full bg-amber-50 rounded-lg p-4 text-left text-sm">
                <p className="text-gray-700 font-semibold mb-2">
                  💡 Want to know when Pengi returns?
                </p>
                <p className="text-gray-600 text-s mb-3">
                  Reminders are off right now.
                </p>
                <p className="text-gray-700 font-medium mb-2">
                  To turn them on:
                </p>
                <ul className="space-y-1.5 text-gray-600">
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-0.5">•</span>
                    <span>Click the small button next to the web address</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-0.5">•</span>
                    <span>Find "Notifications" and choose "Allow"</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-gray-400 mt-0.5">•</span>
                    <span>Refresh this page</span>
                  </li>
                </ul>
              </div>

              <NormalButton onClick={handleDismiss} colorType="green">
                Got it
              </NormalButton>
            </>
          ) : (
            <>
              {/* Prompt for default state */}
              <div className="w-full bg-blue-50 rounded-lg p-4">
                <p className="text-gray-700 text-sm">
                  💡 Want to know when Pengi returns?
                  <br />
                  We can notify you!
                </p>
              </div>

              {/* Buttons */}
              <div className="flex flex-row gap-3 w-full">
                <NormalButton onClick={handleDismiss} colorType="gray" className="flex-1">
                <span className="py-1 text-lg">
                    {laterButtonText}
                  </span>
                </NormalButton>
                <NormalButton onClick={handleEnableClick} colorType="green" className="flex-1">
                  <span className="py-1 text-lg">
                    {enableButtonText}
                  </span>
                </NormalButton>
              </div>
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default NotificationPermissionModal;
